/**
 * Submit and approve — an admin submits AND approves an employee's timesheet
 * for the range shown on the Timesheet dashboard (one week, or a month).
 * Captures the manager name + hourly rate, the same two values the Approvals
 * page captures when approving.
 *
 * If past weekdays are blank the backend answers 422 `incomplete_timesheet`;
 * the modal then lists those days and offers "Submit anyway", which re-posts
 * with `acknowledge_incomplete`. Mount it only while open — its state starts
 * fresh on every mount.
 *
 * Not built on ConfirmDialog: that component confirms on Enter, which is wrong
 * for a form with text inputs.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";

import { api, extractErrorMessage } from "@/lib/api";
import type {
  AdminSubmitRequest,
  AdminSubmitResult,
  EmployeeTimesheetWeek,
} from "@/lib/types";
import { useAuthStore } from "@/store/auth";

type Props = {
  employee: { user_id: string; display_name: string };
  /** ISO dates, inclusive — the dashboard range. */
  from: string;
  to: string;
  weeks: EmployeeTimesheetWeek[];
  totalSeconds: number;
  billableSeconds: number;
  onClose: () => void;
  onDone: (result: AdminSubmitResult) => void;
};

/** Unfilled weekdays from a 422 `incomplete_timesheet`, or null for any other error. */
function incompleteDays(err: unknown): string[] | null {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response
    ?.data?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    (detail as { type?: string }).type === "incomplete_timesheet"
  ) {
    return (detail as { unfilled_days?: string[] }).unfilled_days ?? [];
  }
  return null;
}

function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function fmtDate(iso: string): string {
  return parseISODate(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function fmtDay(iso: string): string {
  return parseISODate(iso).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function hours(seconds: number): string {
  return (seconds / 3600).toFixed(2);
}

export function SubmitOnBehalfModal({
  employee,
  from,
  to,
  weeks,
  totalSeconds,
  billableSeconds,
  onClose,
  onDone,
}: Props) {
  const queryClient = useQueryClient();
  const adminName = useAuthStore((s) => s.user?.display_name ?? "");

  const [manager, setManager] = useState(adminName);
  const [rate, setRate] = useState("");
  // Second approval stage in the same step — releases the employee's own
  // Excel/PDF download for the weeks approved here. Off by default.
  const [releaseDownload, setReleaseDownload] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the backend reports blank weekdays; switches the modal to the
  // "Submit anyway" confirmation.
  const [unfilled, setUnfilled] = useState<string[] | null>(null);

  const submit = useMutation({
    mutationFn: async (acknowledge: boolean) => {
      const body: AdminSubmitRequest = {
        from_date: from,
        to_date: to,
        manager_name: manager.trim() || null,
        hourly_rate_usd: rate.trim() === "" ? null : Number(rate),
        acknowledge_incomplete: acknowledge,
        release_download: releaseDownload,
      };
      return (
        await api.post<AdminSubmitResult>(
          `/admin/timesheets/employee/${employee.user_id}/submit`,
          body,
        )
      ).data;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "employee-timesheet"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "team-timesheet"] });
      queryClient.invalidateQueries({ queryKey: ["admin-timesheet-submissions"] });
      onDone(result);
    },
    onError: (err) => {
      const days = incompleteDays(err);
      if (days) {
        setError(null);
        setUnfilled(days);
        return;
      }
      setError(extractErrorMessage(err, "Could not submit this timesheet."));
    },
  });

  // Escape closes, unless a request is in flight. No Enter handler on purpose.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !submit.isPending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submit.isPending]);

  const approvedCount = weeks.filter((w) => w.status === "approved").length;
  const pendingCount = weeks.filter((w) => w.status === "submitted").length;
  const openCount = weeks.length - approvedCount;

  const onConfirm = () => {
    setError(null);
    const trimmed = rate.trim();
    if (trimmed !== "") {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 0 || n > 99999.99) {
        setError("Enter an hourly rate between 0 and 99999.99, or leave it blank.");
        return;
      }
    }
    submit.mutate(unfilled !== null);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="submit-on-behalf-title"
      onClick={() => {
        if (!submit.isPending) onClose();
      }}
      style={overlay}
    >
      <div onClick={(e) => e.stopPropagation()} style={panel}>
        <h2 id="submit-on-behalf-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 4px" }}>
          Submit and approve timesheet
        </h2>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--mute)" }}>
          {employee.display_name} · {fmtDate(from)} – {fmtDate(to)}
        </p>

        {unfilled ? (
          <>
            <p style={{ margin: "0 0 10px", fontSize: 14, color: "var(--body)", lineHeight: 1.5 }}>
              {unfilled.length === 1
                ? "1 weekday in this period has no hours logged. Submitting anyway approves the timesheet with that day blank."
                : `${unfilled.length} weekdays in this period have no hours logged. Submitting anyway approves the timesheet with those days blank.`}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "0 0 16px" }}>
              {unfilled.map((d) => (
                <span key={d} style={chip}>
                  {fmtDay(d)}
                </span>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={summary}>
              <div>
                <div style={fieldLabel}>Weeks</div>
                <div style={summaryValue}>{weeks.length}</div>
              </div>
              <div>
                <div style={fieldLabel}>Active hours</div>
                <div style={summaryValue}>{hours(billableSeconds)}</div>
              </div>
              <div>
                <div style={fieldLabel}>Total hours</div>
                <div style={summaryValue}>{hours(totalSeconds)}</div>
              </div>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "var(--mute)", lineHeight: 1.5 }}>
              {openCount} of {weeks.length} week{weeks.length === 1 ? "" : "s"} not yet approved
              {pendingCount > 0 ? ` (${pendingCount} already submitted by the employee)` : ""}.
              Weeks with no logged hours are skipped
              {approvedCount > 0 ? " and approved weeks are left unchanged" : ""}.
            </p>

            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={fieldLabel}>Manager name</span>
              <input
                type="text"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
                maxLength={255}
                autoFocus
                style={input}
              />
            </label>
            <label style={{ display: "block", marginBottom: 12 }}>
              <span style={fieldLabel}>Hourly rate in USD (optional)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                max={99999.99}
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                style={input}
              />
            </label>
            <label style={checkboxRow}>
              <input
                type="checkbox"
                checked={releaseDownload}
                onChange={(e) => setReleaseDownload(e.target.checked)}
                style={{ margin: 0, accentColor: "var(--ink)" }}
              />
              <span>Also release the employee's Excel and PDF download</span>
            </label>
            <p style={{ margin: "0 0 14px", fontSize: 12, color: "var(--mute)" }}>
              Approved weeks are locked and cannot be reopened.
            </p>
          </>
        )}

        {error && (
          <p role="alert" style={errorBox}>
            {error}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} disabled={submit.isPending} style={secondaryBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submit.isPending}
            style={{
              ...primaryBtn,
              cursor: submit.isPending ? "wait" : "pointer",
              opacity: submit.isPending ? 0.6 : 1,
            }}
          >
            {submit.isPending ? "Submitting…" : unfilled ? "Submit anyway" : "Submit and approve"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  backdropFilter: "blur(4px)",
  display: "grid",
  placeItems: "center",
  zIndex: 100,
};

const panel: CSSProperties = {
  width: "min(460px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow: "var(--shadow-modal)",
};

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const checkboxRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
  fontSize: 13,
  color: "var(--body)",
  cursor: "pointer",
};

const summary: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: 12,
  padding: 12,
  marginBottom: 12,
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
};

const summaryValue: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const chip: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "3px 8px",
  fontSize: 12,
  color: "var(--body)",
};

const errorBox: CSSProperties = {
  padding: "8px 12px",
  fontSize: 13,
  color: "var(--danger)",
  background: "color-mix(in srgb, var(--danger) 8%, transparent)",
  border: "1px solid color-mix(in srgb, var(--danger) 30%, transparent)",
  borderRadius: 4,
  marginBottom: 12,
};

const secondaryBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
};

const primaryBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
};
